import { useState } from 'react'
import { color, FormatUtils } from '@my/util'
import { ChevronDown, ChevronUp } from '@tamagui/lucide-icons-2'
import { ActionButton, SimpleText, XStack, YStack } from '@my/ui'

export const FinancialCard = ({ title, total, themeColor, details, ...rest }: FinancialCardProps) => {
  const [isExpanded, setIsExpanded] = useState(rest.isExpanded || false);
  const includeDescription = !rest.removeDescriptionLabel;
  const mainContent = <>
    {/* Details */}
    {details?.map((item, index) => (
      <DetailItem
        key={index}
        label={item?.label}
        title={title}
        value={item?.value}
        trend={item?.trend}
      />
    ))}
    {total && <>
      <XStack bg={color.lightLavender} height={1} marginVertical="$3" />
      <DetailSummary total={total?.value} trend={total?.trend} />
    </>}
  </>
  return rest.justMainContent ? mainContent : (
    <XStack minWidth={250} style={rest.rootStyle || {}}>
      <ActionButton
        unstyled
        borderWidth={1}
        borderRadius={10}
        borderColor={themeColor}
        onPress={() => setIsExpanded((prev) => !prev)}
        minWidth={250}
        justifyContent={'center'}
        $platform-web={{ minHeight: 70 }}
      >
        {/* Card Header */}
        <CardHeader
          title={title}
          value={total?.value}
          isExpanded={isExpanded}
          themeColor={themeColor}
        />
        {isExpanded && <XStack bg={color.lightLavender} height={1} />}
        {/* Expanded Details */}
        {isExpanded && (
          <YStack padding="$4">
            {includeDescription && <SimpleText marginBottom={10}>Description</SimpleText>}
            {mainContent}
          </YStack>
        )}
      </ActionButton>
    </XStack>
  )
}

const CardHeader = ({ title, value, isExpanded, themeColor }: CardHeaderProps) => {
  const { lightGreen, opaqueBlack, grey, opaqueRed } = color
  const backgroundColor = { Assets: lightGreen, Liabilities: opaqueRed }[title] || opaqueBlack
  return (
    <XStack flexDirection="row" justifyContent="space-between" alignItems="center" padding="$4">
      <SimpleText maxWidth={220} minWidth={200} fontSize={18} fontWeight="bold" color={color.darkNavy}>
        {!isExpanded && value ? `${title} - ${FormatUtils.currency.cashFormat(value, {decimalPlaces: 0})}` : title}
      </SimpleText>
      <ActionButton
        unstyled
        disabled
        color={grey}
        borderRadius={3}
        backgroundColor={backgroundColor}
        icon={isExpanded ? ChevronUp : ChevronDown}
        style={{ height: 18, width: 18, alignItems: 'center', justifyContent: 'center' }}
      />
    </XStack>
  )
}

const DetailItem = ({ label, value, trend, title }: SnapShotDetailItemProps) => {
  const { lightRed, parrotGreen } = color
  // Conditional arrow color for Liabilities
  const arrowColor = title
    ? title === 'Liabilities'
      ? trend == 'loss'
        ? lightRed
        : parrotGreen
      : trend == 'gain'
        ? parrotGreen
        : lightRed
    : color.lightGrey

  return (
    <XStack flexDirection="row" justifyContent="space-between" alignItems="center" marginBottom="$2">
      <SimpleText fontSize={16} fontWeight={'700'} color={color.grey}>
        {label}
      </SimpleText>
      <SimpleText color={arrowColor} fontSize={16} fontWeight={'700'}>
        {trend == 'gain' ? `↑` : trend == 'loss' ? `↓` : null}
        <SimpleText fontSize={16} fontWeight={'700'} color={color.lightGrey}>
          {FormatUtils.currency.cashFormat(value, {decimalPlaces: 0})}
        </SimpleText>
      </SimpleText>
    </XStack>
  )
}

const DetailSummary = ({ total, trend }: DetailSummaryProps) => (
  <XStack flexDirection="row" justifyContent="space-between">
    <SimpleText fontWeight="bold" fontSize={17} color={color.grey}>
      Total
    </SimpleText>
    <SimpleText
      fontSize={17}
      fontWeight={'700'}
      color={trend == 'gain' ? color?.parrotGreen : color?.lightRed}
    >
      {trend == 'gain' ? `↑` : trend == 'loss' ? `↓` : ''}
      <SimpleText fontSize={17} fontWeight={'700'} color={color.lightGrey}>
        {FormatUtils.currency.cashFormat(total, {decimalPlaces: 0})}
      </SimpleText>
    </SimpleText>
  </XStack>
)
